import { useMemo } from 'react';
import { css } from '@emotion/react';
import { useTranslation } from '@frontend/i18n';
import { useTranslationTokens } from '@frontend/i18n-tokens';
import { getFullName } from '@frontend/name';
import { formatPhoneNumber } from '@frontend/phone-numbers';
import { theme } from '@frontend/theme';
import { Button, Heading, PopoverDialog, Text, usePopoverDialog } from '@frontend/design-system';
import { useGetWaitlistInfiniteQuery } from '../../hooks';
import { useWaitlistShallowStore } from '../../stores';

type PersonInfo = {
  name: string;
  phoneNumber: string;
};

export const SelectedPersonInfoHeader = () => {
  const { t } = useTranslation('schedule-quick-fill');
  const tokens = useTranslationTokens();
  const { selectedPersonIds, selectedLocationId } = useWaitlistShallowStore('selectedPersonIds', 'selectedLocationId');
  const dialogProps = usePopoverDialog({ placement: 'bottom-start' });
  const infiniteQuery = useGetWaitlistInfiniteQuery({ locationId: selectedLocationId });

  const personInfoList = useMemo<PersonInfo[]>(() => {
    if (!infiniteQuery.data) return [];
    const allRows = infiniteQuery.data.pages.flatMap((page) => page.rows);
    const personMap = new Map(allRows.map((row) => [row.personId, row.person]));

    return selectedPersonIds.reduce<PersonInfo[]>((acc, personId) => {
      const person = personMap.get(personId);
      if (person) {
        acc.push({
          name: getFullName(person),
          phoneNumber: person.mobilePhone || person.homePhone || person.workPhone || '',
        });
      }
      return acc;
    }, []);
  }, [infiniteQuery.data, selectedPersonIds]);

  const numberOfRecipients = selectedPersonIds.length;
  const noOfContactsText = `${numberOfRecipients} ${
    numberOfRecipients === 1 ? tokens.individualContact : tokens.contacts
  }`;

  return (
    <header>
      <Text size='small' color='subdued'>
        {t('Sending to')}
      </Text>
      <div css={{ display: 'flex', alignItems: 'center', gap: theme.spacing(1) }}>
        <Heading level={3}>{noOfContactsText}</Heading>
        <Button
          size='small'
          variant='secondary'
          iconName='preview'
          css={{ padding: theme.spacing(0.5), height: 24 }}
          {...dialogProps.getTriggerProps()}
        />
      </div>
      <PopoverDialog {...dialogProps.getDialogProps()} css={{ width: 250 }}>
        <Text weight='semibold' size='small' css={{ marginBottom: theme.spacing(1) }}>
          {t('Message Recipients')}
        </Text>
        <PersonInfoList personInfoList={personInfoList} />
      </PopoverDialog>
    </header>
  );
};

const PersonInfoList = ({ personInfoList }: { personInfoList: PersonInfo[] }) => (
  <div css={personListContainer}>
    {personInfoList.map((person, index) => (
      <div key={index} css={[personInfoItem]}>
        <Text size='small' weight='medium'>
          {person.name} - {formatPhoneNumber(person.phoneNumber)}
        </Text>
      </div>
    ))}
  </div>
);

const personListContainer = css({
  maxHeight: 400,
  border: `1px solid ${theme.colors.neutral20}`,
  borderRadius: theme.borderRadius.medium,
  overflow: 'auto',
});

const personInfoItem = css({
  padding: theme.spacing(1),
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(0.5),
  ':not(:last-child)': {
    borderBottom: `1px solid ${theme.colors.neutral20}`,
  },
});
